import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getAllAdherentsList } from "../../../../services/adherentServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_FILTERS,
  buildAdherentStats,
  cleanAdherents,
  filterAdherents,
  getUniqueOptions,
  paginateItems,
  sortAdherents,
} from "../utils/adminAdherentsUtils";

export function useAdminAdherents() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [adherents, setAdherents] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchAdherents = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getAllAdherentsList();
      const safeData = cleanAdherents(Array.isArray(data) ? data : []);

      setAdherents(safeData);

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Adhérents actualisés.", "success");
      }
    } catch (error) {
      console.error("Erreur récupération adhérents :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des adhérents.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAdherents({ silent: true });
  }, [fetchAdherents]);

  const filteredAdherents = useMemo(() => {
    return sortAdherents(filterAdherents(adherents, filters), filters.sort);
  }, [adherents, filters]);

  const pagination = useMemo(() => {
    return paginateItems(filteredAdherents, page, filters.perPage);
  }, [filteredAdherents, filters.perPage, page]);

  const stats = useMemo(() => {
    return buildAdherentStats(adherents);
  }, [adherents]);

  const clubOptions = useMemo(() => {
    return getUniqueOptions(adherents, "club");
  }, [adherents]);

  const departementOptions = useMemo(() => {
    return getUniqueOptions(adherents, "departement");
  }, [adherents]);

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const openAdherentDrawer = useCallback((adherent) => {
    setSelectedAdherent(adherent);
    setIsDrawerOpen(true);
  }, []);

  const closeAdherentDrawer = useCallback(() => {
    setSelectedAdherent(null);
    setIsDrawerOpen(false);
  }, []);

  return {
    adherents,
    filteredAdherents,
    paginatedAdherents: pagination.items,
    filters,
    stats,
    clubOptions,
    departementOptions,
    selectedAdherent,
    isDrawerOpen,
    loading,
    refreshing,
    page: pagination.page,
    totalPages: pagination.totalPages,
    fetchAdherents,
    updateFilter,
    resetFilters,
    setPage,
    openAdherentDrawer,
    closeAdherentDrawer,
  };
}